/**
 * Input scope by HVAC system type — KAPL ENG VRF IN 001 (VRV / VRF Heat Load
 * Input Matrix). The server decides, from the project's system, which input
 * fields apply (app/services/input_scope.py); every input screen asks it here
 * rather than deciding for itself.
 *
 *   const sc = useInputScope(api, authFetch, projectId);
 *   sc.show("room", "pressure_pa")   → false on a VRF project (hidden, value kept)
 *   sc.required("room", "length_m")  → true  (matrix M)
 *   sc.editable("room", "area_m2")   → false (matrix A — derived)
 *   sc.code / sc.help                → the matrix code and its guidance text
 *
 * A project whose system has no issued matrix (cleanroom, unset) gets
 * applies=false and everything shows exactly as before.
 */

import React from "react";

export function scopeHelper(d) {
  const applies = !!(d && d.applies);
  const f = (rec, key) => (d && d.fields ? d.fields[`${rec}.${key}`] : null) || null;
  return {
    data: d,
    loaded: d !== undefined,
    applies,
    doc: d && d.document,
    show: (rec, key) => !applies || !!(f(rec, key) && f(rec, key).show),
    required: (rec, key) => applies && !!(f(rec, key) && f(rec, key).required),
    editable: (rec, key) => !applies || !f(rec, key) || f(rec, key).editable !== false,
    code: (rec, key) => (applies && f(rec, key) ? f(rec, key).state : null),
    help: (rec, key) => {
      const x = applies ? f(rec, key) : null;
      return x ? [x.help, x.why].filter(Boolean).join("\n") || null : null;
    },
  };
}

/** The project's input scope; `tick` re-reads it (e.g. after the system changes).
 *  `withHidden` false skips the list of stored values on hidden fields (a
 *  screen that does not show that list need not pay for reading every room). */
export function useInputScope(api, authFetch, projectId, tick, withHidden = true) {
  const [d, setD] = React.useState(undefined);
  React.useEffect(() => {
    if (!projectId) { setD(null); return; }
    let live = true;
    authFetch(`${api}/api/eng/projects/${projectId}/input-scope${withHidden ? "" : "?hidden=false"}`)
      .then(r => (r.ok ? r.json() : null)).then(x => { if (live) setD(x); })
      .catch(() => { if (live) setD(null); });
    return () => { live = false; };
  }, [api, authFetch, projectId, tick, withHidden]);
  return React.useMemo(() => scopeHelper(d), [d]);
}

const TONE = { M: ["#FEF2F2", "#B91C1C"], C: ["#FFFBEB", "#B45309"], O: ["#F3F4F6", "#4B5563"],
               A: ["#EFF6FF", "#1D4ED8"], D: ["#F5F3FF", "#6D28D9"], OEM: ["#ECFEFF", "#0E7490"] };

/** The matrix code beside a label (M / C / O / A …), its guidance as the tooltip. */
export function ScopeMark({ sc, rec, k }) {
  if (!sc || !sc.applies) return null;
  const code = sc.code(rec, k);
  const t = TONE[code];
  if (!t) return null;
  return (
    <span title={sc.help(rec, k) || undefined}
      style={{ marginLeft: 5, padding: "0 4px", borderRadius: 4, fontSize: 9.5, fontWeight: 700,
               background: t[0], color: t[1], cursor: "help", textTransform: "none", letterSpacing: 0 }}>
      {code}
    </span>
  );
}

/** One line saying which matrix governs the screen, and what it hides. */
export function ScopeNote({ sc, children }) {
  if (!sc || !sc.applies) return null;
  const doc = sc.doc || {};
  return (
    <div style={{ fontSize: 12, color: "#1E3A8A", background: "#EFF6FF", border: "1px solid #BFDBFE",
                  borderRadius: 8, padding: "7px 11px", marginBottom: 10, lineHeight: 1.45 }}>
      <b>{doc.title || "Input matrix"}</b> ({doc.document} {doc.revision}) — only the parameters it gives a
      {" "}{sc.data?.system_category} project are shown; <b>M</b> mandatory · <b>C</b> conditional (hover for the
      {" "}condition) · <b>O</b> optional · <b>A</b> derived. Hidden fields keep any value already stored.
      {children ? <> {children}</> : null}
    </div>
  );
}

/** Values the project holds on fields its matrix hides — kept, never asked. */
export function HiddenValues({ sc, roomsOnly }) {
  const hv = sc && sc.applies ? sc.data?.hidden_values : null;
  if (!hv) return null;
  const rooms = hv.rooms || [];
  const proj = roomsOnly ? [] : (hv.project || []);
  if (!rooms.length && !proj.length) return null;
  const blocking = rooms.filter(r => r.fields.some(f => f.blocks));
  return (
    <details style={{ fontSize: 12, color: "#4B5563", marginBottom: 10 }}>
      <summary style={{ cursor: "pointer" }}>
        {rooms.length ? `${rooms.length} room(s)` : ""}{rooms.length && proj.length ? " and " : ""}
        {proj.length ? `${proj.length} project value(s)` : ""} hold values on fields this system does not use —
        kept, not shown, not asked for{blocking.length ? ` · ${blocking.length} room(s) still carry a cleanroom class (the VRF engine blocks them)` : ""}.
      </summary>
      <div style={{ marginTop: 4, paddingLeft: 12 }}>
        {proj.map(p => <div key={`${p.record}.${p.key}`}>Project · {p.label}: {String(p.value)}{p.used ? " (mapped until the VRF input is stated)" : ""}</div>)}
        {rooms.map(r => (
          <div key={r.room_id}>{r.room}: {r.fields.map(f => `${f.label} ${f.value}${f.blocks ? " — blocks the VRF engine" : f.used ? " (mapped until the VRF input is stated)" : ""}`).join("; ")}</div>
        ))}
      </div>
    </details>
  );
}

/** The matrix's missing inputs for a project — read once and shared (the
 *  server runs the VRF engine to answer, so it is not fetched per room). */
export function useScopeMissing(api, authFetch, projectId, tick) {
  const [d, setD] = React.useState(null);
  React.useEffect(() => {
    if (!projectId) return;
    let live = true;
    authFetch(`${api}/api/eng/projects/${projectId}/input-scope/missing`)
      .then(r => (r.ok ? r.json() : null)).then(x => { if (live) setD(x); }).catch(() => { });
    return () => { live = false; };
  }, [api, authFetch, projectId, tick]);
  return d;
}

/** What is still to be stated, by the project's input matrix — every question
 *  labelled with the matrix record(s) it serves. `roomId` narrows it to one
 *  room; `data` is a result already read with useScopeMissing. */
export function ScopeMissing({ api, authFetch, projectId, roomId, tick, data }) {
  const own = useScopeMissing(api, authFetch, data === undefined ? projectId : null, tick);
  const d = data === undefined ? own : data;
  if (!d || !d.applies) return null;
  const qs = roomId === undefined ? d.questions : d.questions.filter(q => q.room_id === roomId);
  if (roomId !== undefined && !qs.length) {
    return <div style={{ fontSize: 12, color: "#166534", marginBottom: 8 }}>Nothing left to state for this room under the input matrix.</div>;
  }
  const conflicts = Object.fromEntries((d.conflicts || []).map(c => [c.id, c]));
  const groups = [];
  const at = {};
  qs.forEach(q => {
    const g = q.room_id == null ? "Project" : (q.room || `Room ${q.room_id}`);
    if (!(g in at)) { at[g] = groups.length; groups.push([g, []]); }
    groups[at[g]][1].push(q);
  });
  const line = (q, i) => {
    const t = TONE[q.matrix_code] || ["#F3F4F6", "#4B5563"];
    const c = q.conflict && conflicts[q.conflict];
    return (
      <div key={i} style={{ padding: "2px 0", display: "flex", gap: 6, alignItems: "baseline" }}>
        <span style={{ padding: "0 4px", borderRadius: 4, fontSize: 9.5, fontWeight: 700, background: t[0], color: t[1] }}>
          {q.matrix_code || "—"}</span>
        <span style={{ color: "#6B7280", minWidth: 92 }}>{(q.matrix || []).slice(0, 2).join(", ") || "engine"}</span>
        <span>{q.ask || q.question}<span style={{ color: "#9CA3AF" }}> · {q.where}</span>
          {c && c.decided && <span title={`${c.decided}\nApplied: ${c.kept}`} style={{ marginLeft: 6, color: "#047857", cursor: "help" }}>
            KAPL decision ({c.id})</span>}
          {c && !c.decided && <span title={`${c.question}\nKept: ${c.kept}`} style={{ marginLeft: 6, color: "#92400E", cursor: "help" }}>
            asked though the matrix makes it {q.matrix_code === "C" ? "conditional" : q.matrix_code === "O" ? "optional" : "different"} — {c.id}</span>}</span>
      </div>
    );
  };
  return (
    <div style={{ fontSize: 12, marginBottom: 10 }}>
      {roomId === undefined && (
        <div style={{ color: "#92400E", marginBottom: 4 }}>
          {d.count} input(s) still to state in {d.rooms_with_questions} room(s) and the project
          {d.kept_by_conflict ? ` — ${d.kept_by_conflict} of them asked because the VRF engine needs them where the matrix would not (questions for KAPL)` : ""}.
        </div>)}
      {groups.map(([g, list]) => (roomId !== undefined ? <div key={g}>{list.map(line)}</div> : (
        <details key={g} style={{ marginBottom: 2 }}>
          <summary style={{ cursor: "pointer" }}>{g} — {list.length}</summary>
          <div style={{ paddingLeft: 12 }}>{list.map(line)}</div>
        </details>)))}
    </div>
  );
}
